import type { PaginationMetadata } from "./common";

// ponytail: fixed module x action matrix, not master data — same rationale as
// UNIT_OF_MEASUREMENT_CATEGORIES: validate against this on both FE (permission
// grid) and BE (schema enum) instead of a manageable permissions table.
// Keys must match api/src/db/seed/permissions.ts exactly (that's what's
// actually seeded into the `permissions` table and assignable to roles).
export const PERMISSION_MODULES = [
  {
    key: "workspaces",
    label: "Workspace",
    description: "Profil bisnis dan konfigurasi workspace toko",
  },
  {
    key: "roles",
    label: "Roles & Hak Akses",
    description: "Pengaturan role pengguna dan wewenang hak akses",
  },
  {
    key: "users",
    label: "Pengguna",
    description: "Manajemen akun pengguna dan status akses",
  },
  {
    key: "sales",
    label: "Penjualan",
    description: "Transaksi kasir dan pesanan kopi / roti",
  },
  {
    key: "receivables",
    label: "Piutang",
    description: "Manajemen piutang pelanggan dan pelunasan",
  },
  {
    key: "expenses",
    label: "Pengeluaran",
    description: "Pencatatan dan verifikasi pengeluaran kas operasional",
  },
  {
    key: "cash_report",
    label: "Kas",
    description: "Laporan arus kas dan saldo kas harian",
  },
  {
    key: "gross_profit_report",
    label: "Laba Kotor",
    description: "Laporan analisis laba kotor, HPP, dan margin keuntungan",
  },
  {
    key: "rider_sales",
    label: "Penjualan Rider",
    description: "Penjualan dan setoran mitra rider keliling",
  },
  {
    key: "rider_payrolls",
    label: "Penggajian Rider",
    description: "Slip gaji rider per periode dan link ke pengeluaran kas",
  },
  {
    key: "products",
    label: "Produk",
    description: "Katalog produk jadi dan harga jual",
  },
  {
    key: "raw_materials",
    label: "Bahan Baku",
    description: "Katalog dan inventori stok bahan baku",
  },
  {
    key: "uoms",
    label: "Satuan",
    description: "Daftar satuan ukuran (gram, ml, pcs, dll)",
  },
  {
    key: "unit_conversions",
    label: "Konversi Satuan",
    description: "Aturan konversi antar satuan ukuran",
  },
  {
    key: "riders",
    label: "Rider",
    description: "Data master mitra rider keliling",
  },
  {
    key: "activity_logs",
    label: "Riwayat Aktivitas",
    description: "Jejak audit perubahan data sebelum dan sesudah oleh pengguna",
  },
  {
    key: "stocks",
    label: "Inventori & Stok",
    description: "Daftar inventori stok, mutasi kartu stok, dan master alasan selisih",
  },
  {
    key: "stock_opnames",
    label: "Stock Opname",
    description: "Stock opname fisik berkala bahan baku dan produk jadi",
  },
  {
    key: "stock_adjustments",
    label: "Penyesuaian Stok",
    description: "Penyesuaian stok ad-hoc karena rusak, tumpah, selisih atau susut",
  },
  {
    key: "stock_transformations",
    label: "Transformasi Stok",
    description: "Repacking bahan baku dan produksi barang jadi",
  },
  {
    key: "opening_stocks",
    label: "Stok Awal",
    description: "Pencatatan saldo stok awal toko dan penetapan HPP awal",
  },
  {
    key: "product_recipes",
    label: "Resep Produk",
    description: "Komposisi bahan baku produk jadi dan kalkulasi HPP otomatis",
  },
  {
    key: "number_sequences",
    label: "Nomor Urut",
    description: "Konfigurasi penomoran otomatis untuk transaksi dan SKU",
  },
  {
    key: "bonus_rules",
    label: "Aturan Bonus Rider",
    description: "Aturan bonus komisi rider keliling per kategori produk",
  },
  {
    key: "notification_schedules",
    label: "Notifikasi Workspace",
    description: "Jadwal notifikasi otomatis (kapan dan channel mana) per workspace",
  },
  {
    key: "notification_channels",
    label: "Channel Notifikasi",
    description:
      "Channel tujuan notifikasi (nomor WhatsApp, alamat email) yang bisa dipakai ulang di berbagai jadwal",
  },
] as const;

// Specific / Domain-specific actions beyond standard CRUD
export interface SpecificPermissionDef {
  key: string;
  moduleKey: PermissionModule;
  label: string;
  description: string;
}

export const SPECIFIC_PERMISSIONS = [
  // Penjualan & Operasional
  {
    key: "sales.void",
    moduleKey: "sales",
    label: "Void / Batalkan Transaksi",
    description:
      "Membatalkan pesanan yang sudah tercetak atau dibayar (mencegah manipulasi kasir)",
  },
  {
    key: "rider_sales.settle",
    moduleKey: "rider_sales",
    label: "Tutup Shift & Rekonsiliasi Rider",
    description:
      "Memvalidasi setoran uang fisik dan sisa produk dari mitra rider keliling",
  },
  {
    key: "rider_sales.void",
    moduleKey: "rider_sales",
    label: "Void / Batalkan Penjualan Rider",
    description:
      "Membatalkan penjualan rider yang sudah Done/Paid (mencegah manipulasi setoran rider)",
  },
  {
    key: "receivables.verify",
    moduleKey: "receivables",
    label: "Verifikasi Pelunasan Piutang",
    description:
      "Memverifikasi bukti transfer dan menyetujui status pelunasan piutang pelanggan",
  },

  // Keuangan & Kas
  {
    key: "expenses.approve",
    moduleKey: "expenses",
    label: "Persetujuan (Approval) Pengeluaran",
    description:
      "Menyetujui pencairan uang kas toko untuk pengeluaran yang diajukan staf",
  },
  {
    key: "expenses.delete_posted",
    moduleKey: "expenses",
    label: "Hapus Belanja yang Sudah Selesai",
    description:
      "Menghapus pengeluaran berstatus selesai (done) dan membalikkan stok serta riwayat biaya",
  },
  {
    key: "cash_report.export",
    moduleKey: "cash_report",
    label: "Export Laporan Kas",
    description:
      "Mengunduh rekap arus kas dan saldo harian toko ke format file",
  },

  // Master Data & Logistik
  {
    key: "stock_opnames.complete",
    moduleKey: "stock_opnames",
    label: "Selesaikan Stock Opname",
    description:
      "Memvalidasi dan memposting selisih fisik opname menjadi mutasi kartu stok permanen",
  },
  {
    key: "stock_opnames.cancel",
    moduleKey: "stock_opnames",
    label: "Batalkan Stock Opname",
    description:
      "Membatalkan sesi opname atau membalikkan mutasi opname yang sudah selesai",
  },
  {
    key: "stock_adjustments.cancel",
    moduleKey: "stock_adjustments",
    label: "Batalkan Penyesuaian Stok",
    description:
      "Membatalkan penyesuaian stok dan membalikkan mutasi kartu stok",
  },
  {
    key: "stock_transformations.complete",
    moduleKey: "stock_transformations",
    label: "Selesaikan Transformasi Stok",
    description:
      "Memvalidasi dan memposting mutasi repacking atau produksi menjadi kartu stok",
  },
  {
    key: "stock_transformations.cancel",
    moduleKey: "stock_transformations",
    label: "Batalkan Transformasi Stok",
    description:
      "Membatalkan transformasi stok dan membalikkan mutasi kartu stok",
  },
  {
    key: "opening_stocks.complete",
    moduleKey: "opening_stocks",
    label: "Selesaikan Stok Awal",
    description:
      "Memvalidasi dan memposting saldo stok awal serta harga barang menjadi mutasi permanen",
  },
  {
    key: "unit_conversions.update_factor",
    moduleKey: "unit_conversions",
    label: "Ubah Faktor Konversi",
    description:
      "Mengubah nilai rasio konversi satuan atau pasangan satuan — berdampak pada perhitungan HPP produk",
  },

  // Audit & Riwayat

  // Pengaturan & Hak Akses
  {
    key: "number_sequences.reset",
    moduleKey: "number_sequences",
    label: "Reset Nomor Urut",
    description:
      "Mengembalikan nomor urut ke angka awal — berisiko duplikasi jika disalahgunakan",
  },
  {
    key: "workspaces.apply_template",
    moduleKey: "workspaces",
    label: "Terapkan Template Workspace",
    description:
      "Menerapkan template awal (produk, harga, dll) ke sebuah workspace",
  },
] as const;

export type SpecificPermission = (typeof SPECIFIC_PERMISSIONS)[number]["key"];

// Logical grouping of modules into major business features
export interface PermissionGroup {
  key: string;
  label: string;
  description: string;
  modules: readonly PermissionModule[];
  specificPermissions?: readonly SpecificPermission[];
}

export const PERMISSION_GROUPS = [
  {
    key: "sales",
    label: "Penjualan & Operasional",
    description:
      "Transaksi kasir, penjualan rider keliling, dan pengelolaan piutang pelanggan",
    modules: ["sales", "rider_sales", "receivables"],
    specificPermissions: [
      "sales.void",
      "rider_sales.settle",
      "rider_sales.void",
      "receivables.verify",
    ],
  },
  {
    key: "finance",
    label: "Keuangan & Kas",
    description:
      "Pencatatan pengeluaran operasional dan pemantauan arus kas harian",
    modules: ["expenses", "cash_report"],
    specificPermissions: [
      "expenses.approve",
      "expenses.delete_posted",
      "cash_report.export",
    ],
  },
  {
    key: "master_data",
    label: "Inventori & Master Data",
    description:
      "Daftar stok, kartu stok, katalog produk, bahan baku, satuan ukuran, konversi satuan, dan rider",
    modules: [
      "stocks",
      "products",
      "raw_materials",
      "uoms",
      "unit_conversions",
      "riders",
      "stock_opnames",
      "stock_adjustments",
      "stock_transformations",
      "opening_stocks",
      "product_recipes",
    ],
    specificPermissions: [
      "stock_opnames.complete",
      "stock_opnames.cancel",
      "stock_adjustments.cancel",
      "stock_transformations.complete",
      "stock_transformations.cancel",
      "opening_stocks.complete",
      "unit_conversions.update_factor",
    ],
  },
  {
    key: "audit",
    label: "Audit & Riwayat Aktivitas",
    description:
      "Pencatatan jejak audit perubahan data sebelum dan sesudah oleh pengguna",
    modules: ["activity_logs"],
    specificPermissions: [],
  },
  {
    key: "settings",
    label: "Pengaturan & Hak Akses",
    description:
      "Pengaturan profil toko, akun pengguna, dan konfigurasi wewenang role",
    modules: [
      "workspaces",
      "users",
      "roles",
      "number_sequences",
      "bonus_rules",
      "notification_schedules",
      "notification_channels",
    ],
    specificPermissions: [
      "number_sequences.reset",
      "workspaces.apply_template",
    ],
  },
] as const satisfies readonly PermissionGroup[];

// Modules rendered under the "Master Data" section of the permission grid (legacy reference)
export const MASTER_DATA_PERMISSION_MODULES = [
  "stocks",
  "products",
  "raw_materials",
  "uoms",
  "unit_conversions",
  "riders",
] as const;

export const PERMISSION_ACTIONS = [
  { key: "view", label: "Lihat" },
  { key: "create", label: "Tambah" },
  { key: "update", label: "Ubah" },
  { key: "delete", label: "Hapus" },
] as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[number]["key"];
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number]["key"];
export type StandardPermission = Exclude<
  `${PermissionModule}.${PermissionAction}`,
  "unit_conversions.update"
>;
export type Permission = StandardPermission | SpecificPermission;

export const STANDARD_PERMISSIONS: StandardPermission[] =
  PERMISSION_MODULES.flatMap((m) =>
    PERMISSION_ACTIONS.map((a) => `${m.key}.${a.key}`),
  ).filter((p): p is StandardPermission => p !== "unit_conversions.update");

export const PERMISSIONS: Permission[] = [
  ...STANDARD_PERMISSIONS,
  ...SPECIFIC_PERMISSIONS.map((sp) => sp.key as SpecificPermission),
];

export interface Role {
  id: string;
  name: string;
  description?: string;
  permissions: Permission[];
  isSystem: boolean;
  canSwitchWorkspace: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleReq {
  name: string;
  description?: string;
  permissions: Permission[];
}

export interface UpdateRoleReq {
  name?: string;
  description?: string;
  permissions?: Permission[];
}

export interface GetRoleReq {
  q?: string;
}

export interface RoleList {
  items: Role[];
  pagination: PaginationMetadata;
}
